import React from "react";
import { useNavigate } from "react-router-dom";
import { ChevronRight, X } from "lucide-react";
import { GenericTag } from "@ui-components/Tag/GenericTag";
import GenericButton from "@ui-components/Button/GenericButton";
import { viewHref } from "@shared/navigation/tabs/ViewsProvider";
import type { AttentionItem } from "../attention/needsAttention";
import type { FirewallEventGroup } from "../events/events.types";
import { eventName } from "../events/eventsFormat";
import type { FirewallPolicy } from "../policies/policies.types";
import { RuleUsage } from "../policies/PoliciesCard";
import { NO_READING, timeAgo } from "../overview/overviewFormat";
import { connectionPath } from "../connectionPath";
import type { Reading } from "../readings/useFwaasReading";
import type { MapNode } from "./serviceMap";

/** The top of every inspector: what kind of thing, its name, one line about it, ✕. */
export function InspectorHead({
    kicker,
    title,
    subtitle,
    titleRef,
    onClose,
}: {
    kicker: string;
    title: string;
    subtitle?: React.ReactNode;
    /** Focused when the inspector opens from somewhere else on the page. */
    titleRef?: React.Ref<HTMLHeadingElement>;
    onClose: () => void;
}) {
    return (
        <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
                <p className="text-xs font-semibold uppercase tracking-wider text-semantic-text-secondary">{kicker}</p>
                <h3 ref={titleRef} tabIndex={-1} className="text-base font-semibold break-words focus:outline-none">
                    {title}
                </h3>
                {subtitle ? <p className="text-sm text-semantic-text-secondary">{subtitle}</p> : null}
            </div>
            <button
                type="button"
                onClick={onClose}
                aria-label="Close details"
                className="grid place-items-center w-8 h-8 shrink-0 rounded-lg border border-semantic-border-primary bg-semantic-bg-primary text-semantic-text-secondary hover:text-semantic-text-primary"
            >
                <X size={14} aria-hidden />
            </button>
        </div>
    );
}

/** A labelled block of the inspector. */
export function InspectorSection({ label, children }: { label: string; children: React.ReactNode }) {
    return (
        <section className="flex flex-col gap-1" aria-label={label}>
            <h4 className="text-xs font-semibold uppercase tracking-wider text-semantic-text-secondary">{label}</h4>
            {children}
        </section>
    );
}

/** What Observations says about the selected thing, so the inspector explains its color. */
export function InspectorAttention({ items }: { items: AttentionItem[] }) {
    if (items.length === 0) return null;
    return (
        <InspectorSection label="Observations">
            <ul className="flex flex-col gap-2">
                {items.map((item) => (
                    <li key={item.id} className="rounded-lg border border-semantic-border-warning-secondary bg-semantic-bg-primary p-3 text-sm">
                        <p className="font-semibold">{item.title}</p>
                        <p className="text-semantic-text-secondary">{item.detail}</p>
                        {item.evidence.length > 0 && (
                            <p className="flex flex-wrap gap-1.5 mt-2">
                                {item.evidence.map((e) => (
                                    <GenericTag key={e.label} label={e.label} variant={e.tone === "ok" ? "success" : "neutral"} size="small" />
                                ))}
                            </p>
                        )}
                    </li>
                ))}
            </ul>
        </InspectorSection>
    );
}

/** A list of rows in the inspector; a row with `onClick` leads somewhere and says so. */
export function InspectorRows({ children }: { children: React.ReactNode }) {
    return <ul className="rounded-lg border border-semantic-border-primary bg-semantic-bg-primary overflow-hidden">{children}</ul>;
}

export function InspectorRow({
    title,
    detail,
    aside,
    onClick,
    href,
}: {
    title: React.ReactNode;
    detail?: React.ReactNode;
    aside?: React.ReactNode;
    onClick?: () => void;
    /** The page `onClick` opens, for the gestures of views (13D); none for a row that selects a node. */
    href?: string;
}) {
    const body = (
        <>
            <span className="flex-1 min-w-0 break-words">
                {title}
                {detail ? <span className="block text-xs text-semantic-text-secondary">{detail}</span> : null}
            </span>
            {aside}
            {onClick ? <ChevronRight size={14} aria-hidden className="text-semantic-text-secondary shrink-0" /> : null}
        </>
    );
    return (
        <li className="border-b border-semantic-border-primary last:border-b-0 text-sm">
            {onClick ? (
                <button type="button" {...viewHref(href)} onClick={onClick} className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-semantic-bg-secondary">
                    {body}
                </button>
            ) : (
                <div className="flex items-center gap-3 px-3 py-2">{body}</div>
            )}
        </li>
    );
}

/** A list that has nothing to show yet (`undefined`) or could not be read (`null`). */
function ReadingState({ reading }: { reading: null | undefined }) {
    return <p className="text-sm text-semantic-text-secondary">{reading === undefined ? "Reading…" : NO_READING}</p>;
}

/** A name the backend withheld reads as hidden, never as blank. */
const named = (names: (string | null)[]) => names.map((name) => name ?? "Hidden").join(", ");

/** A rule's name; with `onClick`, it shows the rule in the Policies table. */
function RuleName({ rule, onClick }: { rule: FirewallPolicy; onClick?: (ruleId: number) => void }) {
    const name = rule.name ?? <span className="italic">Hidden name</span>;
    if (!onClick) return <>{name}</>;
    return (
        <button
            type="button"
            onClick={() => onClick(rule.id)}
            title="Show in Policies"
            className="text-left font-medium text-semantic-text-brand hover:underline"
        >
            {name}
        </button>
    );
}

/** Rules, in the firewall's order, each with the interfaces it joins and how much it is used. */
export function RuleList({
    rules,
    empty,
    onRuleClick,
}: {
    rules: Reading<FirewallPolicy[]>;
    empty: string;
    onRuleClick?: (ruleId: number) => void;
}) {
    if (rules == null) return <ReadingState reading={rules} />;
    if (rules.length === 0) return <p className="text-sm text-semantic-text-secondary">{empty}</p>;
    return (
        <InspectorRows>
            {rules.map((rule) => (
                <InspectorRow
                    key={rule.id}
                    title={<RuleName rule={rule} onClick={onRuleClick} />}
                    detail={`${named(rule.from)} → ${named(rule.to)}`}
                    aside={<RuleUsage policy={rule} />}
                />
            ))}
        </InspectorRows>
    );
}

const EVENT_LEVEL_TAG = {
    error: { label: "Error", variant: "error" },
    warning: { label: "Warning", variant: "warning" },
    info: { label: "Info", variant: "neutral" },
} as const;

/**
 * The events of the selected node, one row each: what happened in plain words, how
 * often and when last (CTX-1272). A table did not fit the panel: each name broke over
 * three lines and its pager took more room than the rows.
 */
export function EventList({ events, empty }: { events: Reading<FirewallEventGroup[]>; empty: string }) {
    if (events == null) return <ReadingState reading={events} />;
    if (events.length === 0) return <p className="text-sm text-semantic-text-secondary">{empty}</p>;
    return (
        <InspectorRows>
            {events.map((event) => {
                const tag = EVENT_LEVEL_TAG[event.level];
                return (
                    <InspectorRow
                        key={`${event.level}|${event.text}|${event.tunnel ?? ""}`}
                        title={<span title={event.text}>{eventName(event.text)}</span>}
                        detail={`${event.count.toLocaleString("en-US")} ${event.count === 1 ? "time" : "times"} · last ${timeAgo(event.last_at)}`}
                        aside={<GenericTag label={tag.label} variant={tag.variant} size="small" />}
                    />
                );
            })}
        </InspectorRows>
    );
}

/**
 * "View connection" goes to the connection's page in the current view, like every link of
 * the dashboard (CTX-1395, which undoes the exception of CTX-1278). A new view comes only
 * from the usual gestures — right click → Add view, Cmd/Ctrl+click, middle click — which
 * `viewHref` registers and the views provider handles before this click runs. Back returns
 * to the firewall on the same node (`?node=`). A node that stands for several connections
 * lists them, each a link; one with nowhere to go has no button.
 */
const VIEW_GESTURE_HINT = "Right-click to open in a new view";

export function ConnectionLinks({ node }: { node: MapNode }) {
    const navigate = useNavigate();
    const type = node.connectionType;
    if (!type || node.connections.length === 0) return null;
    const rows = node.connections.map((item) => ({ item, href: connectionPath({ type, item }) }));
    if (rows.length === 1) {
        const { href } = rows[0];
        if (!href) return null;
        return (
            <div>
                <GenericButton
                    variant="brand"
                    size="sm"
                    label="View connection"
                    title={VIEW_GESTURE_HINT}
                    {...viewHref(href)}
                    onClick={() => navigate(href)}
                />
            </div>
        );
    }
    return (
        <InspectorSection label="Connections">
            <InspectorRows>
                {rows.map(({ item, href }) => (
                    <InspectorRow
                        key={item.id || item.subscriptionId}
                        title={item.subscriptionId || item.id}
                        onClick={href ? () => navigate(href) : undefined}
                        href={href ?? undefined}
                    />
                ))}
            </InspectorRows>
        </InspectorSection>
    );
}
